import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import routes from "../../../../routes/routes";

import {
  CategoryRoom,
  categoryRoomsApiSlice,
} from "../../../../api/categoryRooms/categoryRoomsApiSlice";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  resolveEntrySequence,
  useActiveProductCategory,
} from "../../../../util/categoryConfig";
import { parseBookingQuery, stringifyBookingQuery } from "../url/bookingQuery";
import { BookingQuery } from "../url/bookingQuerySchema";
import type { UseCase } from "../../../../util/useCases";

/**
 * Where picking a room leads (#3069): that room's week view (M3), so the
 * customer sees when it is free before choosing a time. A slot there links
 * back into this flow's time step with the room, its city and the category
 * filled in (`modules/roomWeek/bookingLink.ts`). What the customer already
 * entered travels along in the same query format — the day (the week shown)
 * and the head count and event, which the slot's link carries on.
 */
export const weekViewForPickedRoom = (
  query: BookingQuery,
  room: Pick<CategoryRoom, "groupId">
): string => {
  const path = generatePath(routes.roomWeekPath, { groupId: room.groupId });
  const search = stringifyBookingQuery({
    checkInDate: query.checkInDate,
    numberOfGuests: query.numberOfGuests,
    eventType: query.eventType,
  });
  return search ? `${path}?${search}` : path;
};

/** The URL back at the room list: no room, step 0, the rest kept. */
export const queryWithoutRoom = (query: BookingQuery): BookingQuery => {
  const next = { ...query };
  delete next.objectId;
  delete next.step;
  return next;
};

/**
 * The object-first entry (#3035, M1): a category whose booking flow opens on
 * its rooms rather than on the date (ADR-0026 §4, §Z).
 *
 * ── How it sits on the flow ───────────────────────────────────────────────
 *
 * It is a screen IN FRONT OF the existing step machine, not a reordering of
 * it. The step order, `clampStep` and every deep link stay exactly as they
 * are; what changes is only what step 0 shows:
 *
 *   1. no room picked yet → the category's rooms (`CategoryRoomPicker`);
 *      picking one opens its week view (#3069), whose slots link back here;
 *   2. a room picked → the usual time step, showing that room instead of the
 *      city/district choice;
 *   3. step 1 → the usual dated search, narrowed to that room — with its price
 *      for the chosen time, its opening-hours hint, or "not free then".
 *
 * The picked room lives in the URL as `objectId` — the parameter the room
 * week view's links (#3036) carry into step 0 — so a refresh, a shared link
 * and the browser's back button all keep it. Those links also write the
 * room's city, because the dated search runs per city, and district "all", so
 * a room whose district the address leaves blank is still found
 * (`modules/roomWeek/bookingLink.ts`).
 *
 * `useIsObjectFirst` is the module's one switch-on check: everything here is
 * inert for a category that did not choose it, and nothing else asks.
 */
export const useIsObjectFirst = (useCase: UseCase): boolean => {
  const categoryKey = useActiveProductCategory();
  return useSelector(
    (state: RootState) =>
      resolveEntrySequence(state.tenantConfig?.config, useCase, categoryKey) ===
      "objectFirst"
  );
};

export const useObjectFirstEntry = (useCase: UseCase) => {
  const location = useLocation();
  const navigate = useNavigate();
  const categoryKey = useActiveProductCategory();
  const viewerId = useSelector(
    (state: RootState) => state.account?.profile?.id ?? null
  );
  const isObjectFirst = useIsObjectFirst(useCase);

  const pickedObjectId = isObjectFirst
    ? parseBookingQuery(location.search).objectId ?? null
    : null;

  // READ, not subscribe. The subscription is `CategoryRoomsSubscription`,
  // which the shell mounts only for an object-first category — so a flow that
  // did not choose it never touches this slice at all, not even a skipped
  // query (which RTK still checks against the store's middleware).
  const dispatch = useAppDispatch();
  const args = useMemo(
    () => (isObjectFirst && categoryKey ? { categoryKey, viewerId } : null),
    [isObjectFirst, categoryKey, viewerId]
  );
  // One memoised selector per argument, not a new one per render: RTK's
  // `select(args)` builds a fresh selector each call, and a fresh selector
  // returns a fresh object on every dispatch — every screen using this hook
  // would re-render on every store change.
  const selectRooms = useMemo(
    () => (args ? categoryRoomsApiSlice.endpoints.getCategoryRooms.select(args) : null),
    [args]
  );
  const roomsQuery = useSelector((state: RootState) =>
    selectRooms ? selectRooms(state) : null
  );
  const rooms = roomsQuery?.data;
  const refetchRooms = useCallback(() => {
    if (!args) return;
    // A one-off forced fetch; the mounted subscription keeps the result.
    dispatch(
      categoryRoomsApiSlice.endpoints.getCategoryRooms.initiate(args, {
        forceRefetch: true,
      })
    );
  }, [args, dispatch]);

  const pickedRoom: CategoryRoom | null = useMemo(
    () =>
      pickedObjectId ? rooms?.find((r) => r.groupId === pickedObjectId) ?? null : null,
    [rooms, pickedObjectId]
  );

  /** Pick a room: on to its week view. A push, so Back returns here. */
  const pickRoom = useCallback(
    (room: CategoryRoom) => {
      navigate(weekViewForPickedRoom(parseBookingQuery(location.search), room), {
        state: { fromRoomList: true },
      });
    },
    [location.search, navigate]
  );

  /** Back from the time step to the room list. Dates already chosen are kept. */
  const backToRooms = useCallback(() => {
    const search = stringifyBookingQuery(
      queryWithoutRoom(parseBookingQuery(location.search))
    );
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
  }, [location.pathname, location.search, navigate]);

  return {
    isObjectFirst,
    /** The picked room's id from the URL, or null — also while the list loads. */
    pickedObjectId,
    /** The picked room itself, once the list has it. */
    pickedRoom,
    rooms,
    /** What `CategoryRoomsSubscription` subscribes with, or null when inert. */
    subscriptionArgs: args,
    roomsLoading: !roomsQuery || roomsQuery.isUninitialized || roomsQuery.isLoading,
    roomsFailed: !!roomsQuery?.isError,
    refetchRooms,
    pickRoom,
    backToRooms,
  };
};
